import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/shared/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { AvatarGroup, AvatarGroupCount } from '@/shared/components/ui/avatar';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { PALETTE } from '@/core/lib/colors';
import {
  useProjectMembers,
  useRemoveProjectMember,
  useUpdateProjectMember,
  type ProjectMember,
} from '@/features/projects/public';
import { useConfirm } from '@/shared/hooks';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { AddProjectMemberDialog } from './AddProjectMemberDialog';
import { ASSIGNABLE_PROJECT_ROLES } from '@/core/constants/roles';

const MAX_AVATARS_MD = 3;
const MAX_AVATARS_LG = 5;

function deterministicColor(userId: string): string {
  const hash = userId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

interface MemberBarProps {
  projectId: string;
}

export function MemberBar({ projectId }: MemberBarProps) {
  const { t } = useTranslation('tasks');
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectMember | null>(null);
  const [editRoleValue, setEditRoleValue] = useState('');

  const { data: members = [] } = useProjectMembers(projectId);
  const { mutate: removeMember } = useRemoveProjectMember(projectId);
  const { mutate: updateMemberRole, isPending: isUpdatingRole } = useUpdateProjectMember(projectId);
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  async function handleRemoveMember(memberId: string, name: string) {
    const { confirmed } = await confirm({
      title: t('header.memberList.remove'),
      description: name,
      variant: 'destructive',
    });
    if (confirmed) removeMember(memberId);
  }

  function handleEditRole(member: ProjectMember) {
    setEditingMember(member);
    setEditRoleValue(member.role);
  }

  function handleSaveRole() {
    if (!editingMember) return;
    updateMemberRole({ memberId: editingMember.id, role: editRoleValue });
    setEditingMember(null);
  }

  return (
    <>
      <div className="ml-auto flex items-center gap-2">
        <AvatarGroup className="hidden md:flex">
          {members.slice(0, MAX_AVATARS_LG).map((member, i) => (
            <DropdownMenu key={member.id}>
              <DropdownMenuTrigger asChild>
                <span
                  data-testid={`tasks-header-member-avatar-${member.userId}`}
                  className={i >= MAX_AVATARS_MD ? 'hidden lg:block' : undefined}
                >
                  <UserAvatar
                    name={member.displayName}
                    color={deterministicColor(member.userId)}
                    src={member.avatarUrl ?? undefined}
                    size="sm"
                  />
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{member.displayName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid={`tasks-header-member-edit-role-${member.userId}`}
                  onClick={() => handleEditRole(member)}
                >
                  {t('header.memberList.editRole')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  data-testid={`tasks-header-member-remove-${member.userId}`}
                  onClick={() => handleRemoveMember(member.id, member.displayName)}
                >
                  {t('header.memberList.remove')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
          {members.length > MAX_AVATARS_LG && (
            <AvatarGroupCount className="hidden lg:flex">
              +{members.length - MAX_AVATARS_LG}
            </AvatarGroupCount>
          )}
          {members.length > MAX_AVATARS_MD && (
            <AvatarGroupCount className="hidden md:flex lg:hidden">
              +{members.length - MAX_AVATARS_MD}
            </AvatarGroupCount>
          )}
        </AvatarGroup>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="tasks-header-invite-button"
              onClick={() => setAddMemberOpen(true)}
              className="gap-1.5"
            >
              <UserPlus className="size-4" />
              <span className="hidden md:inline">{t('header.invite')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="md:hidden">{t('header.invite')}</TooltipContent>
        </Tooltip>
      </div>

      <Dialog
        open={editingMember !== null}
        onOpenChange={(open) => {
          if (!open) setEditingMember(null);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingMember?.displayName}</DialogTitle>
          </DialogHeader>
          <Select value={editRoleValue} onValueChange={setEditRoleValue}>
            <SelectTrigger className="w-full" data-testid="tasks-header-edit-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSIGNABLE_PROJECT_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(`header.roles.${r}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMember(null)}>
              {t('header.memberList.cancel')}
            </Button>
            <Button
              onClick={handleSaveRole}
              disabled={isUpdatingRole}
              data-testid="tasks-header-edit-role-save"
            >
              {t('header.memberList.saveRole')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddProjectMemberDialog
        open={addMemberOpen}
        onOpenChange={setAddMemberOpen}
        projectId={projectId}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={confirmOptions?.title ?? ''}
        description={confirmOptions?.description}
        confirmLabel={confirmOptions?.confirmLabel}
        cancelLabel={confirmOptions?.cancelLabel}
        variant={confirmOptions?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
